import { BackofficePrestationType, FileType, Prisma } from "@prisma/client";
import { accountingBuyableItemValues } from "./buyable-item.types";
import { AcceptedTypes } from "./filters.types";
import { InvoiceType } from "./invoice.types";

/* GENERAL ACCOUNTS */
/* Prestations */
export const PROMOTION_CODE = "706502";
export const EXHIBITOR_PROMOTION_CODE = "706508";
export const ADVANCED_STATISTICS_CODE = "706503";
export const EXPORT_EXHIBITORS_MODULE_CODE = "706507";
export const EQUIPMENT_REQUEST_CODE = "706112";
export const EXHIBITOR_PROFILES_PACK_CODE_1 = "706501";
export const EXHIBITOR_PROFILES_PACK_CODE_2 = "706505";
export const EXHIBITOR_PROFILES_PACK_CODE_3 = "706506";
export const ADVANCED_STATISTICS_EXHIBITOR_CODE = "706504";
export const GENERAL_PRESTATION_CODE = "706900";
export const VAT_ACCOUNT_PRESTATION = "445722";
export const CLIENT_ACCOUNT_PRESTATION = "411000";

/* Ticketing */
export const TICKETING_CODE = "706119";
export const CLIENT_ACCOUNT_TICKETING = "411090";
export const VAT_ACCOUNT_TICKETING = "445724";

export const PURCHASING_TICKETING_CODE = "606119";
export const PURCHASING_CLIENT_ACCOUNT_TICKETING = "401090";
const PURCHASING_VAT_ACCOUNT_TICKETING_PREFIX = "4457";

export const getPurchasingVatAccountTicketing = (mounthNumber: number) => {
  const monthNumberString = mounthNumber.toString().padStart(2, "0");
  return `${PURCHASING_VAT_ACCOUNT_TICKETING_PREFIX}${monthNumberString}`;
};

export const PrestationInvoiceLabelMap = {
  TICKETING: "Billetterie",
  ADVANCED_STATISTICS: "Statistiques avancées",
  PROMOTION: "Mise en avant",
  EXHIBITOR_PROFILES_PACK_1_CREDITS: "Profils exposants pack 1",
  EXHIBITOR_PROFILES_PACK_2_CREDITS: "Profils exposants pack 2",
  EXHIBITOR_PROFILES_PACK_3_CREDITS: "Profils exposants pack 3",
  EQUIPMENT_REQUEST: "Matériel",
  EXPORT_EXHIBITORS_MODULE: "Export du module exposants",
} as const;
export type PrestationInvoiceLabel =
  (typeof PrestationInvoiceLabelMap)[keyof typeof PrestationInvoiceLabelMap];

export const InvoiceLabels = Object.values(PrestationInvoiceLabelMap);

export interface AccountingObject {
  pieceDate: Date;
  generalAccount: string;
  invoiceId: string;
  thirdPartyAccount: string;
  label: string;
  dueTime: Date;
  debit: number;
  credit: number;
  journalCode: string;
}

export const includedAccountingObjectValues = {
  orderItems: {
    include: {
      buyableItem: accountingBuyableItemValues,
      ticket: true,
      badge: true,
    },
  },
  onlineDetails: {
    include: {
      invoice: {
        include: {
          file: true,
        },
      },
      user: true,
    },
  },
} satisfies Prisma.OrderInclude;

export const accountingObjectValues = Prisma.validator<Prisma.OrderDefaultArgs>()({
  include: {
    ...includedAccountingObjectValues,
  },
});

export type AccountingObjectPrisma = Prisma.OrderGetPayload<typeof accountingObjectValues>;

export const selectedPurchaseObjectValues = {
  id: true,
  date: true,
  reference: true,
  structure: {
    select: {
      id: true,
      bankDetails: {
        select: {
          id: true,
          counter: true,
        },
      },
    },
  },
  event: {
    select: {
      id: true,
      name: true,
      bankDetails: {
        select: {
          id: true,
        },
      },
      organiser: {
        select: {
          id: true,
          admin: {
            select: {
              connectId: true,
              businessName: true,
              firstName: true,
              lastName: true,
              email: true,
            },
          },
          bankDetails: {
            select: {
              id: true,
              counter: true,
            },
          },
        },
      },
    },
  },
  orderItems: {
    select: {
      basePrice: true,
      vatRate: true,
      finalTotal: true,
      badge: {
        select: {
          badgeType: {
            select: {
              name: true,
            },
          },
        },
      },
      ticket: {
        select: {
          ticketType: {
            select: {
              name: true,
            },
          },
        },
      },
    },
  },
} satisfies Prisma.EventInvoiceSelect;

export const purchaseObjectValues = Prisma.validator<Prisma.EventInvoiceDefaultArgs>()({
  select: {
    ...selectedPurchaseObjectValues,
  },
});

export type PurchaseObjectPrisma = Prisma.EventInvoiceGetPayload<typeof purchaseObjectValues>;

export const includedAccountingEventInvoiceObjectValues = {
  file: true,
  orderItems: {
    include: {
      buyableItem: {
        select: {
          buyableType: true,
        },
      },
    },
  },
  event: {
    select: {
      organiser: {
        select: {
          admin: {
            select: {
              connectId: true,
              businessName: true,
            },
          },
        },
      },
    },
  },
} satisfies Prisma.EventInvoiceInclude;

export const accountingEventInvoiceObjectValues =
  Prisma.validator<Prisma.EventInvoiceDefaultArgs>()({
    include: {
      ...includedAccountingEventInvoiceObjectValues,
    },
  });

export type AccountingEventInvoiceObject = Prisma.EventInvoiceGetPayload<
  typeof accountingEventInvoiceObjectValues
>;

export interface InvoiceItem {
  code: string;
  quantity: number;
  label: string;
  totalTaxLess: number;
  totalTax: number;
  totalTaxIncluded: number;
}
export interface InvoiceObject {
  id: string;
  invoiceDate: Date;
  invoiceId: string;
  invoiceFileType: FileType;
  invoiceKey: string;
  connectEventId: string;
  businessName: string;
  items: InvoiceItem[];
  totalInvoiceTaxLess: number;
  totalInvoiceTax: number;
  totalInvoiceTaxIncluded: number;
}

export interface PurchaseObject {
  invoiceDate: Date;
  generalAccount: string;
  invoiceNumber: string;
  thirdPartyAccount: string;
  businessName: string;
  label: string;
  dueTime: Date;
  debit: number;
  credit: number;
  journalCode: string;
}

export interface AccountingFilters extends Record<string, AcceptedTypes> {
  startDate?: string;
  endDate?: string;
  searchText?: string;
  prestationType?: BackofficePrestationType;
  prestationInvoiceType?: PrestationInvoiceLabel;
  invoiceType?: InvoiceType;
}
